import { createContext, useState, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  SupabaseUser,
  signInWithEmail,
  signUpWithEmail,
  signOut,
  onAuthStateChange,
  getUserSubscription,
  getUserUsage,
  SubscriptionInfo,
  UsageInfo,
  formatAuthError,
  clearPeriodCache,
} from '../../../core/auth/supabase-auth';
import type { AuthErrorCode } from '../../../core/auth/supabase-auth';
import { Session } from '@supabase/supabase-js';
import { warmupBackend } from '../../../core/services/warmup';
import i18n from '../../../i18n';

interface AuthContextType {
  currentUser: SupabaseUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  subscription: SubscriptionInfo | null;
  hasPaidSubscription: boolean;
  subscriptionLoading: boolean;
  usage: UsageInfo | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    metadata?: Record<string, unknown>
  ) => Promise<{ requiresConfirmation: boolean }>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const ERROR_CODE_TO_I18N_KEY: Record<AuthErrorCode, string> = {
  invalid_credentials: 'auth:login.errors.invalidCredentials',
  email_not_confirmed: 'auth:login.errors.emailNotConfirmed',
  email_already_registered: 'auth:register.errors.emailExists',
  rate_limit: 'auth:login.errors.rateLimit',
  network: 'auth:login.errors.network',
  generic: 'auth:login.errors.generic',
};

type AuthErrorContext = 'login' | 'register' | 'logout';

const translateAuthError = (
  code: AuthErrorCode,
  context: AuthErrorContext = 'login'
): string => {
  // For 'generic' code in register context, use register-specific copy
  if (code === 'generic' && context === 'register') {
    return i18n.t('auth:register.errors.generic');
  }
  return i18n.t(ERROR_CODE_TO_I18N_KEY[code]);
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for request deduplication
  const fetchInProgressRef = useRef(false);
  const lastFetchedUserIdRef = useRef<string | null>(null);

  // Fetch subscription and usage data (memoized with deduplication)
  const fetchUserData = useCallback(async (userId: string, force = false) => {
    // Prevent duplicate concurrent requests
    if (fetchInProgressRef.current) {
      return;
    }

    // Skip if we already fetched for this user (unless forced)
    if (!force && lastFetchedUserIdRef.current === userId) {
      return;
    }

    // On a forced refresh (login, checkout, plan change, manual refresh), bust the
    // 1h period-start cache so usage is read for the CURRENT anniversary period
    // rather than a stale one (e.g. right after upgrade or an anniversary rollover).
    if (force) {
      clearPeriodCache();
    }

    fetchInProgressRef.current = true;
    setSubscriptionLoading(true);
    try {
      const [subscriptionData, usageData] = await Promise.all([
        getUserSubscription(userId),
        getUserUsage(userId),
      ]);

      setSubscription(subscriptionData);
      setUsage(usageData);
      lastFetchedUserIdRef.current = userId;
    } catch (err) {
      console.error('❌ [fetchUserData] Failed:', err);
      // If the JWT is corrupted/manipulated, Supabase returns PGRST301 with this signature.
      // Sign the user out so the app doesn't get stuck in a half-logged-in state.
      const message = (err as Error).message || '';
      const isCorruptedJwt =
        message.includes('Not valid base64url') ||
        message.includes('JWSError') ||
        message.includes('JWT expired');
      if (isCorruptedJwt) {
        try {
          await signOut();
        } catch (signOutErr) {
          console.error('❌ [fetchUserData] signOut after corruption failed:', signOutErr);
        }
      }
    } finally {
      fetchInProgressRef.current = false;
      setSubscriptionLoading(false);
    }
  }, []);

  // Refresh user data (for use after operations that change usage)
  const refreshUserData = useCallback(async (): Promise<void> => {
    if (!currentUser) return;
    await fetchUserData(currentUser.id, true); // force=true to bypass deduplication
  }, [currentUser, fetchUserData]);

  // Listen for auth state changes
  useEffect(() => {
    const subscription = onAuthStateChange(async (user, session, event) => {
      // Skip TOKEN_REFRESHED events - no need to refetch user data
      if (event === 'TOKEN_REFRESHED') {
        return;
      }

      setCurrentUser(user);
      setSession(session);
      // Auth state is now known; unblock the AuthProvider gate immediately.
      // Subscription data continues loading via fetchUserData below; consumers
      // that care wait on subscriptionLoading instead of loading.
      setLoading(false);

      if (user) {
        // Fetch subscription and usage data when user logs in
        // Only fetch on SIGNED_IN or INITIAL_SESSION events
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          await fetchUserData(user.id);
          // Warmup backend server to reduce cold start latency
          warmupBackend();
        }
      } else {
        // Clear data when user logs out
        setSubscription(null);
        setUsage(null);
        setSubscriptionLoading(false);
        lastFetchedUserIdRef.current = null;
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserData]);

  // Auto-refresh usage every 5 minutes (was 60 seconds - optimized)
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;

    const intervalId = setInterval(async () => {
      await fetchUserData(userId, true); // force=true for periodic refresh
    }, 300000); // 5 minutes (300 seconds)

    return () => clearInterval(intervalId);
  }, [currentUser?.id, fetchUserData]);

  // When the backend signals compute hours exhausted, route the user to the
  // pricing page so they can upgrade. handle403Error in src/api/utils/errorHandler
  // dispatches this event for any 403 with error_code='compute_hours_exhausted'.
  useEffect(() => {
    const onExhausted = () => {
      if (window.location.pathname !== '/pricing') {
        window.location.assign('/pricing');
      }
    };
    window.addEventListener('compute-hours-exhausted', onExhausted);
    return () => window.removeEventListener('compute-hours-exhausted', onExhausted);
  }, []);

  // Login with email and password
  const login = async (email: string, password: string): Promise<void> => {
    setError(null);
    try {
      await signInWithEmail(email, password);
      // User data will be fetched automatically by onAuthStateChange
    } catch (err) {
      const errorMessage = translateAuthError(formatAuthError(err as Error), 'login');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Register with email and password
  const register = async (
    email: string,
    password: string,
    metadata?: Record<string, unknown>
  ): Promise<{ requiresConfirmation: boolean }> => {
    setError(null);
    try {
      const result = await signUpWithEmail(email, password, metadata);
      // When a session is created (no confirmation required), onAuthStateChange
      // fires with SIGNED_IN and AuthContext fetches user data automatically.
      return { requiresConfirmation: result.requiresConfirmation };
    } catch (err) {
      const errorMessage = translateAuthError(formatAuthError(err as Error), 'register');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    setError(null);
    try {
      clearPeriodCache(); // Clear cached period start on logout
      await signOut();
      // State will be cleared automatically by onAuthStateChange
    } catch (err) {
      const errorMessage = translateAuthError(formatAuthError(err as Error), 'logout');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const hasPaidSubscription =
    !!subscription
    && subscription.status === 'active'
    && subscription.plan_name?.toLowerCase() !== 'free';

  const value = {
    currentUser,
    session,
    isAuthenticated: !!currentUser,
    subscription,
    hasPaidSubscription,
    subscriptionLoading,
    usage,
    login,
    register,
    logout,
    refreshUserData,
    loading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
